import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { type ApiError, request } from '@/api/apiUtils';
import { tokenAtom } from '@/atoms/auth';
import { tutorialMarkSignedInAtom } from '@/atoms/tutorial';
import { versionAtom } from '@/atoms/version';
import { companyIdAtom, projectIdAtom } from '@/atoms/workspace';
import { authEntity } from '@/data/entities/auth';
import type { hintsBlock } from '@/enums';
import type { Assets, IUser, LoginType, RegistrationTypes, ResetPasswordTypes, ResponseType } from '@/types';

// ---------------------------------------------------------------------------
// Auth Mutations
// ---------------------------------------------------------------------------

export function useLogin() {
    const setToken = useSetAtom(tokenAtom);
    const markSignedIn = useSetAtom(tutorialMarkSignedInAtom);
    return useMutation<ResponseType<{ token: string }>, ApiError, LoginType>({
        mutationFn: params => request('auth/login', { method: 'POST', body: JSON.stringify(params) }),
        onSuccess: result => {
            if (result.success && result.data.token) {
                setToken(result.data.token);
                markSignedIn();
            }
        },
    });
}

export function useRegistration() {
    const setToken = useSetAtom(tokenAtom);
    const markSignedIn = useSetAtom(tutorialMarkSignedInAtom);
    return useMutation<ResponseType<{ token: string }>, ApiError, RegistrationTypes>({
        mutationFn: params =>
            request('auth/register', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
        onSuccess: result => {
            if (result.success && result.data.token) {
                setToken(result.data.token);
                markSignedIn();
            }
        },
    });
}

export function useLogout() {
    const setToken = useSetAtom(tokenAtom);
    const setVersion = useSetAtom(versionAtom);
    const setCompanyId = useSetAtom(companyIdAtom);
    const setProjectId = useSetAtom(projectIdAtom);
    const queryClient = useQueryClient();
    return useMutation<ResponseType<void>, ApiError, void>({
        mutationFn: () => request('auth/logout', { method: 'POST' }),
        onSuccess: result => {
            if (result.success) {
                setVersion('');
                setToken('');
                setCompanyId(null);
                setProjectId(null);
                queryClient.clear();
            }
        },
    });
}

export function useResetPassword() {
    return useMutation<ResponseType<{ token: string }>, ApiError, ResetPasswordTypes>({
        mutationFn: params =>
            request('auth/reset-password', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
    });
}

export function useForgotPassword() {
    return useMutation<ResponseType<void>, ApiError, { email: string }>({
        mutationFn: params =>
            request('auth/forgot-password', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
    });
}

export function useChangePassword() {
    return useMutation<ResponseType<void>, ApiError, FormData>({
        mutationFn: data => request('user/change-password', { method: 'POST', body: data }),
    });
}

export function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation<ResponseType<IUser>, ApiError, { data: FormData }>({
        mutationFn: ({ data }) => request('/user', { method: 'POST', body: data }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authEntity.fetchers.user.key() });
        },
    });
}

/**
 * Avatar asset ops on the current user. The generic asset hooks skip auth
 * invalidation (their rows fan out to characters/features), so these purpose
 * hooks own the "avatar changed → user query refreshes" fact.
 */
export function useStoreUserAvatar() {
    const queryClient = useQueryClient();
    return useMutation<ResponseType<Assets>, ApiError, FormData>({
        mutationFn: data => request('assets', { method: 'POST', body: data }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authEntity.fetchers.user.key() });
        },
    });
}

export function useDeleteUserAvatar() {
    const queryClient = useQueryClient();
    return useMutation<ResponseType<Assets>, ApiError, { asset_id: number }>({
        mutationFn: ({ asset_id }) => request(`assets/${asset_id}`, { method: 'DELETE' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authEntity.fetchers.user.key() });
        },
    });
}

export function useVerifyEmail() {
    return useMutation<ResponseType<IUser>, ApiError, { id: number; expires: string; hash: string; signature: string }>({
        mutationFn: params =>
            request(`auth/verify-email?expires=${params.expires}&hash=${params.hash}&id=${params.id}&signature=${params.signature}`, {
                method: 'GET',
            }),
    });
}

export function useResendVerifyEmail() {
    return useMutation<ResponseType<IUser>, ApiError, { email: string }>({
        mutationFn: params =>
            request('auth/send-verification-code', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
    });
}

export function useInviteCompanyEmployee() {
    return useMutation<ResponseType<{ message: string }>, ApiError, { company_id: number; email: string; role_id: number }>({
        mutationFn: params =>
            request('invitations/employee', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
    });
}

export function useSendFeedback() {
    return useMutation<ResponseType<{ id: number }>, ApiError, { data: FormData }>({
        mutationFn: ({ data }) => request('feedback', { method: 'POST', body: data }),
    });
}

export function useShowHints() {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, { type: hintsBlock }>({
        mutationFn: params =>
            request('/user/hint-show', {
                method: 'POST',
                body: JSON.stringify(params),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authEntity.fetchers.user.key() });
        },
    });
}

export function useResetHints() {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError>({
        mutationFn: () => request('/user/hint-reset', { method: 'DELETE' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: authEntity.fetchers.user.key() });
        },
    });
}
